import React from 'react';
import { isStaleChunkError, tryStaleChunkRecovery } from '../utils/stale-chunk-recovery';

// v5.16.10 (Peter 2026-05-04): scoped error boundary specifically for the
// QuoteMakerAssistantPanel. Caught: Peter asked the assistant to "correct
// its cost analysis" in Stage 4 Scope Builder; assistant crashed, took the
// whole app with it because the only error boundary was at the app root.
// This boundary contains crashes inside the assistant subtree so the rest
// of the app keeps working — Peter doesn't lose his Stage 4 scope edits
// when a tool-call response choke happens. Reset button remounts the
// assistant cleanly via a `bumpKey` counter that forces a fresh tree.
//
// TS-060 / DEC-COUNCIL-015 T2: extracted verbatim from App.jsx's module-scope
// prelude (behaviour-preserving; only type annotations + this note added).
// Consumed by App.jsx, which wraps <QuoteMakerAssistantPanel> in this boundary.

interface AssistantErrorBoundaryProps {
    // React.Children.only requires exactly one element child (the assistant panel).
    children: React.ReactElement;
}

interface AssistantErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: React.ErrorInfo | null;
    bumpKey: number;
}

export class AssistantErrorBoundary extends React.Component<AssistantErrorBoundaryProps, AssistantErrorBoundaryState> {
    constructor(props: AssistantErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null, bumpKey: 0 };
    }

    static getDerivedStateFromError(error: Error): Partial<AssistantErrorBoundaryState> {
        return { hasError: true, error };
    }

    override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.setState({ error, errorInfo });
        try {
            // eslint-disable-next-line no-console
            console.error('[QM Assistant] Caught render-time error:', error, errorInfo);
        } catch { /* ignore */ }
        // Stale chunk recovery — chunk hash changed (deploy mid-session); auto-reload.
        if (typeof isStaleChunkError === 'function' && isStaleChunkError(error)) {
            try { tryStaleChunkRecovery(error); } catch {}
        }
    }

    handleReset = () => {
        // Remount the inner tree by bumping the key — forces React to discard
        // the crashed subtree and rebuild from scratch with fresh state.
        this.setState(prev => ({
            hasError: false,
            error: null,
            errorInfo: null,
            bumpKey: prev.bumpKey + 1,
        }));
    };

    override render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    position: 'fixed',
                    bottom: '24px',
                    right: '24px',
                    width: 'min(380px, 92vw)',
                    background: '#ffffff',
                    border: '1px solid #fca5a5',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
                    zIndex: 100000,
                    overflow: 'hidden',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
                }} role="alert" aria-live="polite">
                    <div style={{
                        padding: '12px 16px',
                        background: '#fef2f2',
                        borderBottom: '1px solid #fecaca',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}>
                        <span aria-hidden="true" style={{ fontSize: '16px' }}>⚠️</span>
                        <strong style={{ fontSize: '13px', color: '#991b1b' }}>AI Assistant hit a problem</strong>
                    </div>
                    <div style={{ padding: '14px 16px', fontSize: '12.5px', color: '#374151', lineHeight: 1.5 }}>
                        <p style={{ margin: '0 0 10px' }}>
                            The assistant chat panel crashed. Your work in the main app (scope, quotes, files) is unaffected.
                        </p>
                        <p style={{ margin: '0 0 12px', color: '#6b7280', fontSize: '11.5px' }}>
                            Click <strong>Reset assistant</strong> to clear the crashed conversation and reopen the panel cleanly. If it crashes again on the same prompt, please send a screenshot of the technical details.
                        </p>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                                type="button"
                                onClick={this.handleReset}
                                style={{
                                    padding: '7px 14px',
                                    background: 'var(--qm-accent)',
                                    color: 'var(--qm-accent-on)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '12.5px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    minHeight: 'auto',
                                }}
                            >
                                Reset assistant
                            </button>
                            {this.state.error && (
                                <details style={{ flex: '1 1 100%', marginTop: '6px' }}>
                                    <summary style={{ fontSize: '11px', color: '#6b7280', cursor: 'pointer' }}>
                                        Technical details
                                    </summary>
                                    <pre style={{
                                        margin: '6px 0 0',
                                        padding: '8px',
                                        background: '#f9fafb',
                                        borderRadius: '6px',
                                        fontSize: '10.5px',
                                        color: '#dc2626',
                                        overflow: 'auto',
                                        maxHeight: '120px',
                                        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    }}>
                                        {String(this.state.error?.stack || this.state.error || 'Unknown error').slice(0, 1500)}
                                    </pre>
                                </details>
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        // Wrap children with a `key` so reset re-mounts the subtree.
        return React.cloneElement(
            React.Children.only(this.props.children),
            { key: `qm-assistant-mount-${this.state.bumpKey}` },
        );
    }
}
